import React, { useCallback, useMemo, useState } from "react";

import { faArrowDownShortWide } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Popover from "antd/es/popover";
import { defineMessages, useIntl } from "react-intl";

import SortOrder from "@mapmycustomers/shared/enum/SortOrder";
import { Button, SelectField } from "@mapmycustomers/ui";

import SortField, { getSortFieldDisplayName } from "./enum/SortField";
import getSortDirectionNameForField from "./getSortDirectionNameForField";
import styles from "./ListDrawerOrderSelect.module.scss";

const messages = defineMessages({
  apply: {
    id: "listDrawer.sort.apply",
    defaultMessage: "Apply",
    description: "Apply sort button",
  },
  cancel: {
    id: "listDrawer.sort.cancel",
    defaultMessage: "Cancel",
    description: "Cancel sort button",
  },
  sortCriteria: {
    id: "listDrawer.sort.criteria",
    defaultMessage: "Sort Criteria",
    description: "Sort Criteria",
  },
  sortDirection: {
    id: "listDrawer.sort.direction",
    defaultMessage: "Sort Direction",
    description: "Sort Direction",
  },
});

interface Props {
  onChange: (field: SortField, order: SortOrder) => void;
  sort: { field: SortField; order: SortOrder };
}

const ListDrawerOrderSelect: React.FC<Props> = ({ onChange, sort }) => {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const [order, setOrder] = useState<SortOrder>(sort.order);
  const [field, setField] = useState<SortField>(sort.field);

  const sortOptions = useMemo(
    () =>
      [SortField.UPDATED_AT].map((field) => ({
        label: getSortFieldDisplayName(intl, field),
        value: field,
      })),
    [intl]
  );

  const sortOrderOptions = useMemo(
    () =>
      [SortOrder.ASC, SortOrder.DESC].map((order) => ({
        label: getSortDirectionNameForField(intl, field, order),
        value: order,
      })),
    [field, intl]
  );

  const handleCancel = useCallback(() => {
    setOpen(false);
    setOrder(sort.order);
    setField(sort.field);
  }, [sort]);

  const handleApply = useCallback(() => {
    setOpen(false);
    onChange(field, order);
  }, [field, onChange, order]);

  const content = (
    <div className={styles.container}>
      <SelectField<SortField>
        label={intl.formatMessage(messages.sortCriteria)}
        onChange={setField}
        options={sortOptions}
        value={field}
      />
      <SelectField<SortOrder>
        label={intl.formatMessage(messages.sortDirection)}
        onChange={setOrder}
        options={sortOrderOptions}
        value={order}
      />
      <div className={styles.buttons}>
        <Button onClick={handleCancel}>{intl.formatMessage(messages.cancel)}</Button>
        <Button onClick={handleApply} type="primary">
          {intl.formatMessage(messages.apply)}
        </Button>
      </div>
    </div>
  );

  return (
    <Popover content={content} onOpenChange={setOpen} open={open} trigger={["click"]}>
      <Button className={styles.sort} leftIcon={<FontAwesomeIcon icon={faArrowDownShortWide} />}>
        {getSortFieldDisplayName(intl, field)}
      </Button>
    </Popover>
  );
};

export default ListDrawerOrderSelect;
